import { z } from 'zod';
import { isoTimestampSchema, outcomeSchema } from './common.js';
import { assertionSchema, evidenceSchema } from './evidence.js';
import { runSchema, runStepSchema } from './run.js';
/**
 * Messages pushed from the control plane to the browser over the run WebSocket.
 * The UI renders a live timeline from these, so every event carries enough
 * context to be applied on its own without refetching the run.
 */
export const serverEventSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('run.snapshot'),
        run: runSchema,
        steps: z.array(runStepSchema),
    }),
    z.object({
        type: z.literal('run.status'),
        runId: z.string(),
        outcome: outcomeSchema,
        reason: z.string().optional(),
        endedAt: isoTimestampSchema.optional(),
    }),
    z.object({
        type: z.literal('step.status'),
        runId: z.string(),
        step: runStepSchema,
    }),
    z.object({
        type: z.literal('assertion'),
        runId: z.string(),
        stepKey: z.string(),
        assertion: assertionSchema,
    }),
    z.object({
        type: z.literal('evidence'),
        runId: z.string(),
        stepKey: z.string().optional(),
        evidence: evidenceSchema,
    }),
    z.object({
        type: z.literal('log'),
        runId: z.string(),
        level: z.enum(['debug', 'info', 'warn', 'error']),
        message: z.string(),
        at: isoTimestampSchema,
        stepKey: z.string().optional(),
    }),
    z.object({
        type: z.literal('transcript'),
        runId: z.string(),
        speaker: z.enum(['agent', 'customer', 'system']),
        text: z.string(),
        at: isoTimestampSchema,
    }),
    z.object({
        type: z.literal('trigger'),
        runId: z.string(),
        triggerId: z.string(),
        phrase: z.string(),
        kind: z.enum(['Bookmark', 'CallAssist', 'ActionEngine', 'ChecklistItem']),
        spokenAt: isoTimestampSchema,
        observedAt: isoTimestampSchema.optional(),
        observed: z.boolean(),
    }),
    z.object({
        type: z.literal('error'),
        runId: z.string().optional(),
        message: z.string(),
    }),
]);
/** Messages the browser sends to the control plane. */
export const clientEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('subscribe'), runId: z.string() }),
    z.object({ type: z.literal('unsubscribe'), runId: z.string() }),
    z.object({ type: z.literal('ping') }),
]);
/**
 * Line protocol between the runner container and the control plane. The runner
 * writes one JSON object per line on stdout; anything that does not parse is
 * treated as an ordinary log line.
 */
export const runnerMessageSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('runner.ready'), stepCount: z.number().int().nonnegative() }),
    z.object({
        type: z.literal('step.start'),
        stepKey: z.string(),
        title: z.string(),
        group: z.string(),
        stage: z.string(),
        position: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('step.finish'),
        stepKey: z.string(),
        outcome: outcomeSchema,
        durationMs: z.number().int().nonnegative(),
        summary: z.string().optional(),
        reason: z.string().optional(),
        errorDetail: z.string().optional(),
    }),
    z.object({ type: z.literal('assertion'), stepKey: z.string(), assertion: assertionSchema }),
    z.object({
        type: z.literal('evidence'),
        stepKey: z.string().optional(),
        evidence: evidenceSchema,
    }),
    z.object({
        type: z.literal('log'),
        level: z.enum(['debug', 'info', 'warn', 'error']),
        message: z.string(),
        stepKey: z.string().optional(),
    }),
    z.object({
        type: z.literal('transcript'),
        speaker: z.enum(['agent', 'customer', 'system']),
        text: z.string(),
    }),
    z.object({
        type: z.literal('trigger'),
        triggerId: z.string(),
        phrase: z.string(),
        kind: z.enum(['Bookmark', 'CallAssist', 'ActionEngine', 'ChecklistItem']),
        spokenAt: isoTimestampSchema,
        observedAt: isoTimestampSchema.optional(),
        observed: z.boolean(),
    }),
    z.object({ type: z.literal('runner.finish'), outcome: outcomeSchema, reason: z.string().optional() }),
]);
//# sourceMappingURL=events.js.map